import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { forwardRef, useRef, useMemo } from "react";
import CloseIcon from "@mui/icons-material/Close.js";
import { Popover, ClickAwayListener, DialogContent, DialogActions, ThemeProvider, dialogTitleClasses, Typography, IconButton, Button } from "@mui/material";
import { createTheme } from "../../constants/create-theme/create-theme.js";
import { MuiAlert } from "../../themes/alert/alert.js";
import { MuiButton } from "../../themes/button/button.js";
import "color-alpha";
import "../../themes/typography/typography.js";
import { MuiDialog } from "../../themes/dialog/dialog.js";
import "../../themes/external-theme/external-theme.js";
import { MuiFilledInput } from "../../themes/filled-input/filled-input.js";
import "../../themes/pickers-popper/pickers-popper.js";
import { MuiPopover } from "../../themes/popover/popover.js";
import { MuiRadio } from "../../themes/radio/radio.js";
import "../../themes/shadows/shadows.js";
import { MuiSwitch } from "../../themes/switch/switch.js";
import { MuiTooltip } from "../../themes/tooltip/tooltip.js";
import { HANDLE_CLASS } from "./dialog.constants.js";
import { Wrapper, StyledMuiDialog, StyledDialogTitle, HeaderTextWrapper, StyledSubtitle } from "./dialog.styles.js";
import { DraggablePaperComponent } from "./DraggablePaperComponent.js";
import "@mui/material/styles";
import "../../constants/colors/palette.js";
import "../../themes/alert/components/ErrorIcon/ErrorIcon.js";
import "@mui/material/SvgIcon/index.js";
import "../../themes/alert/components/InfoIcon/InfoIcon.js";
import "../../themes/alert/components/SuccessIcon/SuccessIcon.js";
import "../../themes/alert/components/WarningIcon/WarningIcon.js";
import "../../themes/button/styles/external/bordered.styles.js";
import "../../themes/button/styles/external/filled.styles.js";
import "../../themes/button/styles/internal/bordered.styles.js";
import "../../themes/button/styles/internal/borderless.styles.js";
import "../../themes/button/styles/internal/filled.styles.js";
import "../../themes/button/styles/internal/shadowed.styles.js";
import "../../themes/typography/utils/defaultVariantMapping/defaultVariantMapping.js";
import "../../themes/typography/utils/typographyVariants/typographyVariants.js";
import "../../constants/breakpoints/breakpoints.js";
import "../../themes/checkbox/checkbox.js";
import "../../themes/checkbox/components/CheckedIcon/CheckedIcon.js";
import "../../themes/checkbox/components/IndeterminateIcon/IndeterminateIcon.js";
import "../../themes/form-control-label/form-control-label.js";
import "@emotion/react";
import "react-draggable";
const theme = createTheme({
  components: {
    MuiButton,
    MuiPopover,
    MuiFilledInput,
    MuiRadio,
    MuiAlert,
    MuiDialog,
    MuiTooltip,
    MuiSwitch
  }
});
const Dialog = forwardRef(
  ({
    anchorEl,
    anchorOrigin,
    anchorPosition,
    open,
    onClose,
    onClick,
    children,
    subtitle,
    title,
    onSave,
    onCancel,
    hideBackdrop,
    labels,
    disabled,
    disableSave,
    disableClose,
    style,
    width,
    draggable,
    initialPosition,
    variant = "default"
  }, dialogRef) => {
    const headerRef = useRef(null);
    const handleClose = (event, reason) => {
      event.stopPropagation();
      if (disableClose)
        return;
      onClose(event, reason);
    };
    const Header = () => /* @__PURE__ */ jsxs(
      StyledDialogTitle,
      {
        colorVariant: variant,
        ref: headerRef,
        draggable,
        classes: {
          root: `${dialogTitleClasses.root} ${HANDLE_CLASS}`
        },
        children: [
          /* @__PURE__ */ jsxs(HeaderTextWrapper, { children: [
            /* @__PURE__ */ jsx(Typography, { variant: "Lato H5", children: title }),
            /* @__PURE__ */ jsx(StyledSubtitle, { variant: "Lato Emphasized 1", colorVariant: variant, children: subtitle })
          ] }),
          /* @__PURE__ */ jsx(IconButton, { disableRipple: true, onClick: onClose, disabled, children: /* @__PURE__ */ jsx(CloseIcon, {}) })
        ]
      }
    );
    const Actions = () => /* @__PURE__ */ jsxs(Fragment, { children: [
      onCancel && /* @__PURE__ */ jsx(Button, { disabled, onClick: onCancel, variant: "borderless", children: labels == null ? void 0 : labels.cancel }),
      onSave && /* @__PURE__ */ jsx(Button, { disabled: disabled || disableSave, onClick: onSave, variant: "filled", children: labels == null ? void 0 : labels.save })
    ] });
    const MemoizedDraggablePaperComponent = useMemo(() => {
      return (props) => DraggablePaperComponent(props, dialogRef, initialPosition);
    }, [initialPosition, dialogRef]);
    if (anchorEl && !draggable) {
      return /* @__PURE__ */ jsx(
        Popover,
        {
          open,
          elevation: 10,
          anchorEl,
          anchorOrigin,
          anchorPosition,
          style,
          onClose: handleClose,
          componentsProps: {
            root: {
              ref: dialogRef
            }
          },
          children: /* @__PURE__ */ jsx(ClickAwayListener, { onClickAway: handleClose, children: /* @__PURE__ */ jsxs(Wrapper, { onClick, width, children: [
            (title || subtitle) && /* @__PURE__ */ jsx(Header, {}),
            /* @__PURE__ */ jsx(DialogContent, { children }),
            (onSave || onCancel) && /* @__PURE__ */ jsx(DialogActions, { children: /* @__PURE__ */ jsx(Actions, {}) })
          ] }) })
        }
      );
    }
    return /* @__PURE__ */ jsx(ThemeProvider, { theme, children: /* @__PURE__ */ jsx(
      StyledMuiDialog,
      {
        open,
        onClose: handleClose,
        hideBackdrop,
        initialPosition: !!initialPosition,
        ...draggable && {
          PaperComponent: MemoizedDraggablePaperComponent
        },
        PaperProps: {
          onClick,
          elevation: 10,
          style,
          ref: dialogRef
        },
        children: /* @__PURE__ */ jsxs(Wrapper, { width, children: [
          (title || subtitle) && /* @__PURE__ */ jsx(Header, {}),
          /* @__PURE__ */ jsx(DialogContent, { children }),
          (onSave || onCancel) && /* @__PURE__ */ jsx(DialogActions, { children: /* @__PURE__ */ jsx(Actions, {}) })
        ] })
      }
    ) });
  }
);
Dialog.displayName = "Dialog";
export {
  Dialog
};
